/**
 * Native bridge for Xenia mobile app (Capacitor 7).
 *
 * In the web PWA these functions transparently fall back to standard
 * browser APIs. In the iOS / Android wrapper they use the native plugins
 * for camera, geolocation, push notifications, etc.
 *
 * Usage from any component:
 *   import { takeIdPhoto, requestPushPermission, getDeviceLocale } from "@/lib/native";
 *   const photo = await takeIdPhoto();
 */
import { Capacitor } from "@capacitor/core";
import { logDev } from "@/lib/logger";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // "ios" | "android" | "web"

/* ------------------------ Camera (ID-card scan) ------------------------ */

/**
 * Returns a JPEG dataURL of a photo taken with the device camera.
 * On web: opens the OS picker with `capture="environment"`.
 */
export async function takeIdPhoto() {
  if (isNative()) {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const photo = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
      saveToGallery: false,
      promptLabelHeader: "Inquadra il documento",
      promptLabelCancel: "Annulla",
      promptLabelPhoto: "Dalla galleria",
      promptLabelPicture: "Scatta",
    });
    return photo.dataUrl; // "data:image/jpeg;base64,..."
  }
  // Web fallback — caller already has a <input type="file"> path.
  throw new Error("takeIdPhoto is only available on native — use the file input on web.");
}

/* --------------------------- Geolocation --------------------------- */

export async function getCurrentLocation() {
  if (isNative()) {
    const { Geolocation } = await import("@capacitor/geolocation");
    const perm = await Geolocation.requestPermissions();
    if (perm.location !== "granted") return null;
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  }
  if (typeof navigator === "undefined" || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, enableHighAccuracy: true }
    );
  });
}

/* --------------------------- Push notifications --------------------------- */

/**
 * Asks the OS for permission to send push notifications and returns the
 * registration token (APNS on iOS, FCM on Android). On web returns null.
 */
export async function requestPushPermission() {
  if (!isNative()) return null;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return null;
  await PushNotifications.register();
  return new Promise((resolve) => {
    PushNotifications.addListener("registration", (token) => resolve(token.value));
    PushNotifications.addListener("registrationError", () => resolve(null));
    // safety timeout
    setTimeout(() => resolve(null), 8000);
  });
}

/* --------------------------- Device info --------------------------- */

export async function getDeviceLocale() {
  if (isNative()) {
    const { Device } = await import("@capacitor/device");
    const lang = await Device.getLanguageCode();
    return lang.value || "it";
  }
  return (navigator.language || "it").split("-")[0];
}

/* --------------------------- Local preferences --------------------------- */
// Used for secure storage of small key/values (auth fallback, lang choice, ...).
// On web falls back to localStorage.

export async function setPref(key, value) {
  if (isNative()) {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key, value: String(value) });
    return;
  }
  try { localStorage.setItem(key, String(value)); } catch (err) { logDev("setPref: localStorage unavailable", err); }
}

export async function getPref(key) {
  if (isNative()) {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key });
    return value;
  }
  try { return localStorage.getItem(key); } catch (err) { logDev("getPref: localStorage unavailable", err); return null; }
}

/* --------------------------- Splash + status bar --------------------------- */
// Called once from index.js after the React tree is mounted.

export async function hideSplash() {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (err) { logDev("hideSplash skipped", err); }
}

export async function configureStatusBar() {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Light });   // dark text icons
    if (platform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#FBF8F2" });
    }
  } catch (err) { logDev("configureStatusBar skipped", err); }
}

/* --------------------------- App state --------------------------- */
// Hardware back button on Android — exit the app instead of navigating back
// into a non-existent history when at the root.

export async function setupHardwareBackButton(navigate) {
  if (!isNative() || platform() !== "android") return;
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // Confirmation dialog before exit
        if (window.confirm("Vuoi uscire da Xenia?")) {
          App.exitApp();
        }
      }
      void navigate; // reserved
    });
  } catch (err) { logDev("setupHardwareBackButton skipped", err); }
}
